// Processos arquivados (judiciais e administrativos), com busca e desarquivar
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ArchiveRestore, ArrowLeft, Archive, Building2, ChevronRight, Loader2, Scale, Search } from 'lucide-react';

interface ArchivedItem {
  id: string;
  kind: 'judicial' | 'administrative';
  title: string;
  number: string | null;
  client: string | null;
  archivedAt: string | null;
  reason: string | null;
  detail: string | null;
}

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const ArchivedCases = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState<ArchivedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'all' | 'judicial' | 'administrative'>('all');
  const [restoring, setRestoring] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [casesRes, adminRes] = await Promise.all([
        supabase.from('cases').select('id, title, case_number_cnj, case_number, archive_date, archive_reason, court, clients:client_id(name)').eq('archived', true),
        (supabase as any).from('administrative_processes').select('id, title, process_number, administrative_number, archive_date, archive_reason, administrative_agency, procedure_type, updated_at, clients!administrative_processes_client_id_fkey(name)').eq('archived', true),
      ]);
      if (casesRes.error) throw casesRes.error;
      if (adminRes.error) throw adminRes.error;
      const judicial: ArchivedItem[] = (casesRes.data || []).map((c: any) => ({
        id: c.id,
        kind: 'judicial',
        title: c.title,
        number: c.case_number_cnj || c.case_number,
        client: c.clients?.name || null,
        archivedAt: c.archive_date,
        reason: c.archive_reason,
        detail: c.court,
      }));
      const administrative: ArchivedItem[] = (adminRes.data || []).map((p: any) => ({
        id: p.id,
        kind: 'administrative',
        title: p.title,
        number: p.process_number || p.administrative_number,
        client: p.clients?.name || null,
        archivedAt: p.archive_date || p.updated_at,
        reason: p.archive_reason,
        detail: [p.procedure_type, p.administrative_agency].filter(Boolean).join(' · ') || null,
      }));
      setItems([...judicial, ...administrative].sort((a, b) => String(b.archivedAt || '').localeCompare(String(a.archivedAt || ''))));
    } catch (error: any) {
      toast({ title: 'Erro ao carregar arquivados', description: error?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const restore = async (item: ArchivedItem) => {
    if (!window.confirm(`Desarquivar "${item.title}"? Ele volta para a lista de processos ativos.`)) return;
    setRestoring(item.id);
    try {
      const { error } = item.kind === 'judicial'
        ? await supabase.from('cases').update({ archived: false, status: 'Em andamento', archive_date: null } as any).eq('id', item.id)
        : await (supabase as any).from('administrative_processes').update({ archived: false, status: 'em_andamento', archive_date: null }).eq('id', item.id);
      if (error) throw error;
      toast({ title: 'Processo desarquivado', description: item.title });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (error: any) {
      toast({ title: 'Não foi possível desarquivar', description: error?.message, variant: 'destructive' });
    } finally {
      setRestoring(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((i) => {
      if (kind !== 'all' && i.kind !== kind) return false;
      if (!term) return true;
      return [i.title, i.number, i.client, i.detail].some((v) => v?.toLowerCase().includes(term));
    });
  }, [items, search, kind]);

  const counts = {
    all: items.length,
    judicial: items.filter((i) => i.kind === 'judicial').length,
    administrative: items.filter((i) => i.kind === 'administrative').length,
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-4 md:p-6">
      <header className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-1 duration-300 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button type="button" onClick={() => navigate('/cases')} className="mb-2 flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
            <ArrowLeft className="h-3.5 w-3.5" /> Processos
          </button>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a7f52]">Gestão processual</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Arquivados</h1>
          <p className="mt-1 text-sm text-slate-500">
            {counts.all} processo{counts.all === 1 ? '' : 's'} arquivado{counts.all === 1 ? '' : 's'}
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200/80 bg-white p-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título, número ou cliente..."
            className="h-10 rounded-xl border-0 bg-slate-50 pl-9 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
          />
        </div>
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {([
            { key: 'all', label: 'Todos' },
            { key: 'judicial', label: 'Judiciais' },
            { key: 'administrative', label: 'Administrativos' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setKind(tab.key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] transition-colors ${kind === tab.key ? 'bg-white font-semibold text-[#7a6440] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
            >
              {tab.label}
              <span className={`text-[11px] tabular-nums ${kind === tab.key ? 'text-[#9a7f52]' : 'text-slate-400'}`}>{counts[tab.key]}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#c4ab83]" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#faf7f2]">
            <Archive className="h-5 w-5 text-[#c4ab83]" />
          </div>
          <p className="mt-3 font-medium text-slate-800">{search || kind !== 'all' ? 'Nenhum processo encontrado' : 'Nenhum processo arquivado'}</p>
          <p className="mt-1 text-sm text-slate-500">{search || kind !== 'all' ? 'Tente outra busca ou filtro.' : 'Os processos arquivados aparecem aqui.'}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
          <ul className="divide-y divide-slate-100">
            {filtered.map((item) => (
              <li key={`${item.kind}-${item.id}`} className="group flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-[#faf7f2]/60 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => navigate(item.kind === 'judicial' ? `/processo/${item.id}` : `/administrative-process/${item.id}`)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
                    <span className={`inline-flex items-center gap-1 font-semibold uppercase tracking-[0.12em] ${item.kind === 'administrative' ? 'text-blue-600' : 'text-[#9a7f52]'}`}>
                      {item.kind === 'administrative' ? <Building2 className="h-3 w-3" /> : <Scale className="h-3 w-3" />}
                      {item.kind === 'administrative' ? 'Administrativo' : 'Judicial'}
                    </span>
                    {item.number && <><span className="text-slate-300">•</span><span className="font-mono">{item.number}</span></>}
                  </div>
                  <p className="mt-0.5 truncate font-semibold text-slate-900 group-hover:text-[#9a7f52]">{item.title}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {[item.client, item.detail, formatDate(item.archivedAt) && `Arquivado em ${formatDate(item.archivedAt)}`].filter(Boolean).join(' · ')}
                  </p>
                  {item.reason && <p className="mt-1 truncate text-xs text-slate-400">Motivo: {item.reason}</p>}
                </button>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => restore(item)}
                    disabled={restoring === item.id}
                    className="h-8 rounded-lg text-[12px] text-slate-500 hover:bg-white hover:text-[#7a6440]"
                  >
                    {restoring === item.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />}
                    Desarquivar
                  </Button>
                  <ChevronRight className="hidden h-4 w-4 text-slate-300 sm:block" />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ArchivedCases;
export { ArchivedCases };
